import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import config from 'config'
import { format } from 'date-fns'
import itLocale from 'date-fns/locale/it'
import { formatDate } from 'lib/dateLib'

import useSnackbar from 'components/organisms/Snackbar/useSnackbar'

import useInputOutput from 'hooks/useInputOutput'

const useLogs = () => {
  const { t } = useTranslation()

  const { showSnackbarMessage } = useSnackbar()

  const { saveXls } = useInputOutput()

  const [state, setState] = useState({
    isFetching: false,
    error: null,
  })

  const setStartFetching = useCallback(() => {
    setState((s) => ({
      ...s,
      isFetching: true,
      logs: null,
    }))
  }, [setState])

  const setStopUpdating = useCallback(
    (error) => {
      setState((s) => ({
        ...s,
        isFetching: false,
        error: error || null,
      }))
    },
    [setState]
  )

  const setSuccessFetching = useCallback(
    (data) => {
      setState((s) => ({
        ...s,
        isFetching: false,
        logs: data,
        error: null,
      }))
    },
    [setState]
  )

  const _fetchLogs = async (area, sessionId) => {
    const result = await axios.get(`${config.apiBaseUrl}logs/get-url`, {
      params: { area, sessionId },
    })

    if (!result.data.body.success) {
      throw new Error('Server error')
    }

    const resultJSON = await fetch(result.data.body.url)

    return {
      data: { body: await resultJSON.json() },
    }
  }

  const fetchLogs = useCallback(
    async (area, sessionId) => {
      setStartFetching()

      try {
        const result = await _fetchLogs(area, sessionId)
        setSuccessFetching(result.data.body)

        return result
      } catch (e) {
        const message =
          e.response?.data?.message ||
          t('common.error.fetchData', {
            attribute: e.response?.status,
          })

        setStopUpdating()

        showSnackbarMessage({
          severity: 'error',
          message,
        })
      }
    },
    [
      setStartFetching,
      setStopUpdating,
      setSuccessFetching,
      showSnackbarMessage,
      t,
    ]
  )

  const getLogDetailSessionCreation = useCallback(
    (item) => {
      const formatAllWithFractionLabel = 'common.date.formatAllWithFraction'

      let detail = ''
      switch (item.action) {
        case 'updateSessionCreationStartsAt':
        case 'updateSessionCreationEndsAt':
        case 'updatePublicationStartsAt':
        case 'updatePublicationEndsAt':
        case 'updateAssignmentStartsAt':
        case 'updateAssignmentEndsAt':
          detail = format(
            new Date(item.data?.date),
            t(formatAllWithFractionLabel),
            {
              locale: itLocale,
            }
          )
          break
        case 'confirmCars':
        case 'confirmPlanning':
        case 'confirmPricing':
        case 'confirmSession':
          detail = format(
            new Date(item.data?.acceptedAt),
            t(formatAllWithFractionLabel),
            {
              locale: itLocale,
            }
          )
          break
        case 'updatePricing':
          detail = t(`common.logs.detail.fileNameAndNumberOfCars`, {
            fileName: item?.data?.fileName,
            numCars: item?.data?.numCars,
          })
          break
        case 'updateDateBatch':
          detail = t(`common.logs.detail.updateDateBatch`, {
            publicationStart: format(
              new Date(item?.data?.publicationStart),
              t(formatAllWithFractionLabel),
              {
                locale: itLocale,
              }
            ),
            publicationEnd: format(
              new Date(item?.data?.publicationEnd),
              t(formatAllWithFractionLabel),
              {
                locale: itLocale,
              }
            ),
            assignmentStart: format(
              new Date(item?.data?.assignmentStart),
              t(formatAllWithFractionLabel),
              {
                locale: itLocale,
              }
            ),
            assignmentEnd: format(
              new Date(item?.data?.assignmentEnd),
              t(formatAllWithFractionLabel),
              {
                locale: itLocale,
              }
            ),
          })
          break
        default:
          detail = ''
      }

      return detail
    },
    [t]
  )

  const getLogDetailAssignment = useCallback(
    (item) => {
      let detail = ''
      switch (item.action) {
        case 'assignCar':
          detail = t(`common.logs.detail.assignCar`, {
            partnerName:
              item?.pName ??
              item?.piName ??
              t('common.logs.detail.noPartnerName'),
            plate: item?.data?.plate,
            message: item?.data?.message,
          })
          break
        case 'startAssignment':
        case 'confirmAssignment':
        default:
          detail = ''
      }

      return detail
    },
    [t]
  )

  const getLogDetailPartnerRulesGrouping = useCallback(
    (item) => {
      let detail = ''

      if (item.action === 'updatePartnerGroup') {
        detail = t(`common.logs.detail.updatePartnerGroup`, {
          groupId: item?.data?.groupId,
        })
      }

      return detail
    },
    [t]
  )

  const getLogDetailPartnerRulesCosting = useCallback(
    (item) => {
      let detail = ''

      if (item.action === 'updatePartnerCost') {
        detail = t(`common.logs.detail.value`, {
          value: item?.data?.cost,
        })
      }

      return detail
    },
    [t]
  )

  const getLogDetailPartnerRulesParameters = useCallback(
    (item) => {
      let detail = ''

      switch (item.action) {
        case 'updatePartnerRestoration':
          detail = t(`common.logs.detail.value`, {
            value: item?.data?.restoration,
          })
          break
        case 'updatePartnerUseAvailabilityAt':
          detail = t(`common.logs.detail.value`, {
            value: item?.data?.useAvailabilityAt,
          })
          break
        case 'updatePartnerPreviewDays':
          detail = t(`common.logs.detail.value`, {
            value: item?.data?.previewDays,
          })
          break
        case 'updatePartnerWithdrawalPenaltyFreeOfChargeDays':
          detail = t(`common.logs.detail.value`, {
            value: item?.data?.withdrawalPenaltyFreeOfChargeDays,
          })
          break
        case 'updatePartnerWithdrawalPenaltyCostPerDay':
          detail = t(`common.logs.detail.value`, {
            value: item?.data?.withdrawalPenaltyCostPerDay,
          })
          break
        case 'updatePartnerWithdrawalPenaltyMax':
          detail = t(`common.logs.detail.value`, {
            value: item?.data?.withdrawalPenaltyMax,
          })
          break
        case 'updatePartnerFeePreviewFranchiseDays':
          detail = t(`common.logs.detail.value`, {
            value: item?.data?.feePreviewFranchiseDays,
          })
          break
        case 'updatePartnerFeePreviewCostPerDay':
          detail = t(`common.logs.detail.value`, {
            value: item?.data?.feePreviewCostPerDay,
          })
          break
        case 'updatePartnerRestitutionPenaltyCostPerDay':
          detail = t(`common.logs.detail.value`, {
            value: item?.data?.restitutionPenaltyCostPerDay,
          })
          break
        case 'updatePartnerRestitutionPenaltyExtraPreviewDays':
          detail = t(`common.logs.detail.value`, {
            value: item?.data?.restitutionPenaltyExtraPreviewDays,
          })
          break
        case 'updatePartnerRestitutionPenaltyExtraPreviewPenaltyDays':
          detail = t(`common.logs.detail.value`, {
            value: item?.data?.restitutionPenaltyExtraPreviewPenaltyDays,
          })
          break
        case 'updatePartnerUseParkedStatus':
          detail = t(`common.logs.detail.value`, {
            value: item?.data?.useParkedStatus,
          })
          break
        default:
          detail = ''
      }

      return detail
    },
    [t]
  )

  const getLogDetailPartnerInventory = useCallback(
    (item) => {
      let detail = ''
      const formatAllWithFractionLabel = 'common.date.formatAllWithFraction'

      switch (item.action) {
        case 'updateExpires':
          detail = t(`common.logs.detail.updateExpires`, {
            expire: format(
              new Date(item?.data?.expire),
              t(formatAllWithFractionLabel),
              {
                locale: itLocale,
              }
            ),
          })
          break
        case 'returnCarFromPartnerWithRestoration':
          detail = t(`common.logs.detail.returnCarFromPartnerWithRestoration`, {
            partnerName: item?.pName ?? item?.piName,
            restored: t(`common.logs.boolean.${item?.data?.restored}`),
          })
          break
        case 'returnCarFromSixt':
          detail = t(`common.logs.detail.returnCarFromSixt`, {
            restored: t(`common.logs.boolean.${item?.data?.restored}`),
          })
          break
        case 'returnCarToPartner':
          detail = t(`common.logs.detail.returnCarToPartner`, {
            expire: format(
              new Date(item?.expiresAt),
              t(formatAllWithFractionLabel),
              {
                locale: itLocale,
              }
            ),
          })
          break
        case 'returnCar':
          if (item?.data?.exitedAt) {
            detail = t(`common.logs.detail.returnCarWithExit`, {
              enteredAt: formatDate(t, item?.data?.enteredAt),
              exitedAt: formatDate(t, item?.data?.exitedAt),
            })
          } else {
            detail = t(`common.logs.detail.returnCar`, {
              // enteredAt on priority because it seems that stateChangedAt is is updated by the next action
              enteredAt: item?.data?.enteredAt
                ? formatDate(t, item?.data?.enteredAt)
                : formatDate(t, item?.stateChangedAt),
            })
          }
          break
        case 'editDates':
          detail = t(`common.logs.detail.returnCarWithExit`, {
            enteredAt: formatDate(t, item?.data?.enteredAt),
            exitedAt: formatDate(t, item?.data?.exitedAt),
          })
          break
        default:
          detail = ''
      }

      return detail
    },
    [t]
  )

  const getDetail = useCallback(
    (item, area) => {
      let detail = ''
      switch (area) {
        case 'SessionCreation':
          detail = getLogDetailSessionCreation(item)
          break
        case 'Assignment':
          detail = getLogDetailAssignment(item)
          break
        case 'PartnerRulesGrouping':
          detail = getLogDetailPartnerRulesGrouping(item)
          break
        case 'PartnerRulesCosting':
          detail = getLogDetailPartnerRulesCosting(item)
          break
        case 'PartnerRulesParameters':
          detail = getLogDetailPartnerRulesParameters(item)
          break
        case 'PartnerInventory':
          detail = getLogDetailPartnerInventory(item)
          break
        default:
          detail = ''
      }

      return detail
    },
    [
      getLogDetailAssignment,
      getLogDetailPartnerInventory,
      getLogDetailPartnerRulesGrouping,
      getLogDetailPartnerRulesCosting,
      getLogDetailPartnerRulesParameters,
      getLogDetailSessionCreation,
    ]
  )

  const mapLogs = useCallback(
    // eslint-disable-next-line sonarjs/cognitive-complexity
    (data, area) => {
      return [
        [
          t('common.logs.excel.date'),
          t('common.logs.excel.hour'),
          t('common.logs.excel.user'),
          ...(area === 'PartnerInventory'
            ? [t('common.logs.excel.plate')]
            : [t('common.logs.excel.session')]),
          t('common.logs.excel.action'),
          t('common.logs.excel.detail'),
          t('common.logs.excel.comment'),
        ],
        ...data.map((item) => {
          const detail = getDetail(item, area)

          return [
            format(new Date(item.createdAt), t('common.date.formatDMY'), {
              locale: itLocale,
            }),
            format(
              new Date(item.createdAt),
              t('common.date.formatTimeWithFraction'),
              {
                locale: itLocale,
              }
            ),
            item.userEmail,
            ...(area === 'PartnerInventory' ? [item?.plate] : [item.sessionId]),
            t(`common.logs.action.${item.action}`, {
              partnerName: item?.pName ?? item?.piName,
              plate: item?.data?.plate,
            }),
            detail,
            item.comment || '',
          ]
        }),
      ]
    },
    [getDetail, t]
  )

  const saveLogs = useCallback(
    async (name, { area, sessionId }) => {
      setStartFetching()

      try {
        const result = await _fetchLogs(area, sessionId)

        const rows = mapLogs(result.data.body, area)

        await saveXls(name, rows)

        setSuccessFetching(result.data.body)

        /*
        if (
          result.data.body.hasOwnProperty('success') &&
          result.data.body.success &&
          result.data.body.hasOwnProperty('url')
        ) {
          const link = document.createElement('a')
          link.setAttribute('href', result.data.body.url)
          link.setAttribute('download', `${name}.xlsx`)
          link.style.visibility = 'hidden'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          setSuccessFetching({ success: result.data.body.success })
        } else {
          setStopUpdating()
        }
       */
      } catch (e) {
        const message =
          e.response?.data?.message ||
          t('common.error.fetchData', {
            attribute: e.response?.status,
          })

        setStopUpdating()

        showSnackbarMessage({
          severity: 'error',
          message,
        })
      }
    },
    [
      mapLogs,
      saveXls,
      setStartFetching,
      setStopUpdating,
      setSuccessFetching,
      showSnackbarMessage,
      t,
    ]
  )

  return {
    isFetching: state.isFetching,
    logs: state.logs,
    error: state.error,
    fetchLogs,
    saveLogs,
  }
}

export default useLogs
